<template>
  <div>
    <el-badge
      :max="99"
      :value="unreadCount"
      :hidden="unreadCount === 0"
      style="line-height: 25px;margin-top: -5px;"
      @click.native="drawerVisible=true"
    >
      <i class="el-icon-message-solid" />
    </el-badge>
    <el-drawer title="业务通知" :visible.sync="drawerVisible" direction="rtl" @open="handleDrawerOpen">
      <div v-loading="loading" class="drawer-content">
        <div v-if="list.length === 0" class="drawer-empty">暂无通知</div>
        <template v-else>
          <div class="notice-list">
            <div
              v-for="item in list"
              :key="item.uid || item.eventId || item.bizKey || item.createdTime"
              class="notice-item"
              :class="{ 'is-read': item.readStatus === 1 }"
            >
              <div class="notice-header">
                <span class="notice-title">{{ item.title || '消息' }}</span>
                <span class="notice-time">{{ formatTime(item.createdTime) }}</span>
              </div>
              <div class="notice-body">{{ item.message || contentToText(item.content) }}</div>
              <div class="notice-footer">
                <div class="notice-meta">
                  <el-tag size="mini" :type="resolveTagType(item.level)">{{ resolveLevelText(item.level) }}</el-tag>
                  <span class="notice-type">{{ item.messageType || 'UNKNOWN' }}</span>
                </div>
                <div class="notice-actions">
                  <el-button
                    v-if="hasValidAction(item)"
                    size="mini"
                    type="text"
                    @click="handleActionClick(item)"
                  >查看详情</el-button>
                  <el-button
                    v-if="item.readStatus === 0"
                    size="mini"
                    type="primary"
                    plain
                    @click="setRead(item.uid)"
                  >标记已读</el-button>
                  <span v-else class="notice-read">已读</span>
                </div>
              </div>
            </div>
          </div>
        </template>

        <div v-show="list.length > 0" class="drawer-footer">
          <el-button :disabled="unreadCount === 0" type="primary" @click="setAllRead()">全部已读</el-button>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { notificationList, notificationRead, notificationReadAll, notificationUnreadCount } from '@/api/notification';
import { sharedSSEManager } from '@/services/sse/sharedSSEManager';
import { parseTime } from '@/utils';

const NOTIFY_DEDUP_TTL = 30000;
const REFRESH_DEBOUNCE = 400;
const DEFAULT_PAGE_NUM = 1;
const DEFAULT_PAGE_SIZE = 50;

function safeParse (value) {
  if (!value || typeof value !== 'string') {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
}

export default {
  name: 'Notification',
  data () {
    return {
      drawerVisible: false,
      loading: false,
      list: [],
      unreadCount: 0,
      pageNum: DEFAULT_PAGE_NUM,
      pageSize: DEFAULT_PAGE_SIZE,
      sseUnsubscribers: [],
      notifyDedupCache: {},
      refreshTimer: null,
      refreshing: false,
      refreshPending: false
    };
  },
  created () {
    this.bindSSE();
    this.refreshNotificationData();
  },
  beforeDestroy () {
    this.unbindSSE();
    this.clearRefreshTimer();
  },
  methods: {
    normalizeNotifyLevel (level) {
      if (level === 'success' || level === 'warning' || level === 'error') {
        return level;
      }
      return 'info';
    },
    resolveTagType (level) {
      const normalized = this.normalizeNotifyLevel(level);
      if (normalized === 'error') {
        return 'danger';
      }
      if (normalized === 'warning') {
        return 'warning';
      }
      if (normalized === 'success') {
        return 'success';
      }
      return 'info';
    },
    resolveLevelText (level) {
      const normalized = this.normalizeNotifyLevel(level);
      if (normalized === 'success') {
        return '成功';
      }
      if (normalized === 'warning') {
        return '警告';
      }
      if (normalized === 'error') {
        return '失败';
      }
      return '通知';
    },
    normalizeContent (content) {
      if (typeof content === 'undefined' || content === null) {
        return content;
      }
      if (typeof content !== 'string') {
        return content;
      }
      const parsed = safeParse(content);
      return parsed && typeof parsed === 'object' ? parsed : content;
    },
    contentToText (content) {
      if (typeof content === 'string') {
        return content;
      }
      if (content && typeof content.message === 'string') {
        return content.message;
      }
      if (content && typeof content.title === 'string') {
        return content.title;
      }
      if (content && typeof content.reason === 'string') {
        return content.reason;
      }
      return '';
    },
    normalizeNotificationItem (item = {}) {
      const content = this.normalizeContent(item.content);
      const messageType = item.messageType ? String(item.messageType).trim().toUpperCase() : 'UNKNOWN';
      const title = item.title || '消息';
      const message = this.contentToText(content) || item.message || title;
      const action = this.normalizeAction(item.action);

      return {
        ...item,
        uid: item.uid || item.eventId || item.bizKey || '',
        eventId: item.eventId || item.uid || item.bizKey || '',
        messageType,
        title,
        level: this.normalizeNotifyLevel(item.level),
        content,
        message,
        createdTime: item.createdTime || item.createTime || '',
        readStatus: Number(item.readStatus) === 1 ? 1 : 0,
        action
      };
    },
    normalizeAction (action) {
      if (!action || typeof action !== 'object') {
        return null;
      }
      const type = action.type ? String(action.type).trim().toLowerCase() : '';
      const target = action.target ? String(action.target).trim() : '';
      if (!target || (type !== 'route' && type !== 'url')) {
        return null;
      }

      return {
        type,
        target,
        query: action.query && typeof action.query === 'object' ? action.query : undefined,
        newTab: action.newTab !== false
      };
    },
    hasValidAction (item) {
      return !!(item && item.action && item.action.type && item.action.target);
    },
    async handleActionClick (item) {
      if (!this.hasValidAction(item)) {
        this.$message.warning('该通知暂不支持跳转');
        return;
      }

      this.drawerVisible = false;
      const action = item.action;
      if (item.readStatus === 0 && item.uid) {
        await this.setRead(item.uid);
      }

      if (action.type === 'route') {
        const routeLocation = action.query ? { path: action.target, query: action.query } : { path: action.target };
        this.$router.push(routeLocation).catch(error => {
          if (!error || error.name !== 'NavigationDuplicated') {
            console.error('[Notification] route jump failed', error);
          }
        });
        return;
      }

      const opened = action.newTab === false
        ? window.open(action.target, '_self')
        : window.open(action.target, '_blank');
      if (!opened) {
        this.$message.warning('链接打开失败，请检查浏览器拦截设置');
      }
    },
    buildNotificationKey (notification) {
      if (!notification) {
        return '';
      }
      if (notification.eventId) {
        return String(notification.eventId);
      }
      const payload = notification.raw && notification.raw.payload ? notification.raw.payload : null;
      if (payload && payload.bizKey) {
        return String(payload.bizKey);
      }
      const fallback = [
        notification.messageType || 'UNKNOWN',
        notification.createdTime || '',
        this.contentToText(notification.content)
      ].join('|');
      return fallback;
    },
    shouldSkipNotification (notification) {
      const cache = this.notifyDedupCache || {};
      const now = Date.now();
      Object.keys(cache).forEach(key => {
        if (now - cache[key] > NOTIFY_DEDUP_TTL) {
          delete cache[key];
        }
      });

      const key = this.buildNotificationKey(notification);
      if (!key) {
        return false;
      }
      if (cache[key] && now - cache[key] <= NOTIFY_DEDUP_TTL) {
        return true;
      }

      cache[key] = now;
      this.notifyDedupCache = cache;
      return false;
    },
    bindSSE () {
      this.unbindSSE();

      const unsubscribers = [];
      unsubscribers.push(sharedSSEManager.subscribe(notification => {
        this.handleSSENotification(notification);
      }));
      unsubscribers.push(sharedSSEManager.subscribeStatus(status => {
        if (status && status.state === 'error') {
          console.warn('[Notification SSE] Connection error', status);
        }
      }));

      this.sseUnsubscribers = unsubscribers;
    },
    unbindSSE () {
      if (Array.isArray(this.sseUnsubscribers) && this.sseUnsubscribers.length > 0) {
        this.sseUnsubscribers.forEach(unsubscribe => {
          if (typeof unsubscribe === 'function') {
            unsubscribe();
          }
        });
      }
      this.sseUnsubscribers = [];
    },
    handleSSENotification (notification) {
      if (!notification || this.shouldSkipNotification(notification)) {
        return;
      }

      this.$notify({
        title: '消息提醒',
        message: '您有一条新通知',
        type: 'info',
        duration: 3000
      });

      this.dispatchTabFeatureMessage(notification);
      this.scheduleRefresh();
    },
    dispatchTabFeatureMessage (notification) {
      const payload = notification && notification.raw && notification.raw.payload
        ? notification.raw.payload
        : notification;
      window.dispatchEvent(new CustomEvent('finance-tab-feature-message', {
        detail: {
          route: this.$route && this.$route.fullPath ? this.$route.fullPath : '',
          payload,
          notification
        }
      }));
    },
    handleDrawerOpen () {
      this.refreshNotificationData();
    },
    clearRefreshTimer () {
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = null;
      }
    },
    scheduleRefresh () {
      this.clearRefreshTimer();
      this.refreshTimer = setTimeout(() => {
        this.refreshTimer = null;
        this.refreshNotificationData();
      }, REFRESH_DEBOUNCE);
    },
    resolveUnreadCount (res) {
      if (!res || typeof res !== 'object') {
        return this.unreadCount;
      }

      if (typeof res.data === 'number') {
        return res.data;
      }

      const data = res.data && typeof res.data === 'object' ? res.data : res;
      if (typeof data.unreadCount === 'number') {
        return data.unreadCount;
      }

      if (typeof data.count === 'number') {
        return data.count;
      }

      return this.unreadCount;
    },
    async fetchNotificationList () {
      const res = await notificationList({
        pageNum: this.pageNum,
        pageSize: this.pageSize
      });
      const data = res && res.data && typeof res.data === 'object' ? res.data : {};
      const rawList = Array.isArray(data.list) ? data.list : [];
      this.list = rawList.map(item => this.normalizeNotificationItem(item));
      if (typeof data.unreadCount === 'number') {
        this.unreadCount = data.unreadCount;
      }
    },
    async fetchUnreadCount () {
      const res = await notificationUnreadCount();
      this.unreadCount = this.resolveUnreadCount(res);
    },
    async refreshNotificationData () {
      if (this.refreshing) {
        this.refreshPending = true;
        return;
      }

      this.refreshing = true;
      this.loading = true;
      try {
        await Promise.all([
          this.fetchNotificationList(),
          this.fetchUnreadCount()
        ]);
      } catch (error) {
        console.error('[Notification] refresh failed', error);
      } finally {
        this.loading = false;
        this.refreshing = false;
        if (this.refreshPending) {
          this.refreshPending = false;
          this.scheduleRefresh();
        }
      }
    },
    formatTime (value) {
      if (!value) {
        return '-';
      }
      return parseTime(value, '{y}-{m}-{d} {h}:{i}:{s}') || value;
    },
    async setRead (uid) {
      if (!uid) {
        return;
      }

      try {
        this.loading = true;
        await notificationRead({ uids: [uid] });
        await this.refreshNotificationData();
      } finally {
        this.loading = false;
      }
    },
    async setAllRead () {
      if (this.unreadCount === 0) {
        return;
      }

      try {
        this.loading = true;
        await notificationReadAll();
        await this.refreshNotificationData();
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
>>> .el-drawer__header {
  padding: 20px;
  margin: 0px;
}
>>> .el-drawer__body {
  border-top: 1px solid #e0e0e0;
  height: calc(100% - 64px);
}
.drawer-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
}
.drawer-empty {
  color: #909399;
  text-align: center;
  line-height: 80px;
}
.notice-list {
  flex: 1;
  padding: 10px;
  overflow-y: auto;
}
.notice-item {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 10px;
  background: #fff;
}
.notice-item.is-read {
  background: #fafafa;
}
.notice-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.notice-title {
  color: #303133;
  font-weight: 600;
  font-size: 13px;
}
.notice-time {
  color: #909399;
  font-size: 12px;
  white-space: nowrap;
  margin-left: 8px;
}
.notice-body {
  color: #606266;
  word-break: break-word;
  line-height: 1.5;
  margin-bottom: 10px;
}
.notice-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.notice-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}
.notice-type {
  font-size: 12px;
  color: #909399;
}
.notice-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.notice-read {
  color: #909399;
  font-size: 12px;
}
.drawer-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  border-top: 1px solid #ebeef5;
  background: #fff;
}
</style>
